import type { ComponentPropsWithRef, ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '../../lib/utils'
import { Icon, type IconName } from '../icon'

/**
 * Form controls.
 *
 * All three share one shell: `panel2` fill, a `line` border, 7px radius, 30px tall. That is the
 * design's input, and it is deliberately the same box as a secondary button so a field and a
 * button sitting next to each other line up on both edges.
 */

/**
 * Focus changes the **border colour and nothing else** — owner decision, 2026-08-21.
 *
 * No ring, no outline, and no fill change either: `focus:bg-panel` used to be here and went with
 * the outline, because "only the border colour changes" means only the border colour changes. The
 * global rule in `index.css` strips the browser's own outline from form controls; this supplies
 * the replacement indicator.
 *
 * **It is `accent`, the brand green, and not `accent-line`.** The first version used
 * `accent-line` — `#c3dad1` in light mode, a pale mint — and the owner could barely see it, which
 * was fair: the resting border is `#e9e9ec`, so the two differ by almost nothing at 1px. That
 * token exists for hairlines and dividers *inside* accented surfaces, where it sits against a
 * tinted fill. On a plain field it is not a state change, it is a rounding error.
 *
 * `accent` is `#0d5c46` in light and `#2f9e78` in dark, and reads clearly against both `--line`
 * values. When focus is the only indicator, it has to actually indicate.
 *
 * The border stays 1px. Thickening it on focus would reflow the control by a pixel and make every
 * field twitch as it is tabbed through.
 *
 * `transition-colors` is what makes a single-property change read as deliberate rather than as a
 * rendering artefact.
 */
const CONTROL =
  'w-full bg-panel2 border border-line rounded-[7px] text-text text-[13px] ' +
  'placeholder:text-faint transition-colors ' +
  'focus:outline-none focus:ring-0 focus:shadow-none focus:border-accent ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * A labelled control.
 *
 * The label, the hint and the error are wired to the input by id — generated here rather than
 * passed in, because a hand-written id is a duplicate id waiting to happen and the failure is
 * silent: the label simply stops belonging to the field.
 */
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode
  className?: string
}) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('min-w-0', className)}>
      <label htmlFor={id} className="text-muted mb-1.5 block text-[12px]">
        {label}
      </label>
      {children({ id, 'aria-describedby': describedBy })}
      {error ? (
        <p id={errorId} role="alert" className="text-bad mt-1.5 text-[11.5px]">
          {error}
        </p>
      ) : (
        hint && (
          <p id={hintId} className="text-faint mt-1.5 text-[11.5px]">
            {hint}
          </p>
        )
      )}
    </div>
  )
}

export function Input({ className, ...props }: ComponentPropsWithRef<'input'>) {
  return <input className={cn(CONTROL, 'h-[30px] px-[9px]', className)} {...props} />
}

export function Textarea({ className, ...props }: ComponentPropsWithRef<'textarea'>) {
  return <textarea className={cn(CONTROL, 'p-[9px] font-mono text-[12.5px]', className)} {...props} />
}

export function Select({ className, children, ...props }: ComponentPropsWithRef<'select'>) {
  return (
    <div className="relative">
      <select
        className={cn(CONTROL, 'h-[30px] appearance-none pr-7 pl-[9px]', className)}
        {...props}
      >
        {children}
      </select>
      <Icon
        name="chevronDown"
        size={12}
        className="text-faint pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2"
      />
    </div>
  )
}

/**
 * The search box used in the top bar and above every list.
 *
 * The glyph sits inside the control rather than beside it, because a magnifier that is a sibling
 * of the input is a magnifier that drifts out of alignment the first time the layout changes.
 */
export function SearchInput({
  className,
  icon = 'search',
  ...props
}: ComponentPropsWithRef<'input'> & { icon?: IconName }) {
  return (
    <div className={cn('relative', className)}>
      <Icon
        name={icon}
        size={13}
        className="text-faint pointer-events-none absolute top-1/2 left-[9px] -translate-y-1/2"
      />
      <input {...props} className={cn(CONTROL, 'h-[30px] pr-[9px] pl-[28px]')} />
    </div>
  )
}
